import { Shield } from "lucide-react";

// Plain-English privacy policy. Written to satisfy Apple App Store /
// Google Play submission and to give any learner a straight answer about
// what we store, why, and how to get it deleted. This is not legal advice.

const LAST_UPDATED = "February 2026";
const CONTACT_EMAIL = "privacy@liftupreading.com";

export default function Privacy() {
    return (
        <main className="container-page py-16 fade-up" data-testid="privacy-page">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center">
                        <Shield className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <p className="label-eyebrow">Privacy</p>
                </div>
                <h1
                    className="text-4xl sm:text-5xl font-semibold text-[#1C2321] mb-3"
                    style={{ fontFamily: "Outfit" }}
                    data-testid="privacy-title"
                >
                    Privacy policy
                </h1>
                <p className="text-[#819888] mb-10">
                    Last updated: {LAST_UPDATED}. Written in plain English. If any part isn't
                    clear, email us and we'll rewrite it.
                </p>

                <section className="card-rw p-7 mb-6 leading-relaxed text-[#1C2321]">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>
                        Short version
                    </h2>
                    <ul className="space-y-2 list-disc pl-5 text-[#1C2321]">
                        <li>Lift Up Reading is <span className="font-medium">free to use</span>. We are not selling your data. We never will.</li>
                        <li>We store your email, name, password (hashed — we can't read it), lesson progress, and streak so the app can work when you sign back in.</li>
                        <li>Voice recordings you make during read-aloud practice are transcribed by OpenAI's Whisper and then <span className="font-medium">not kept</span>.</li>
                        <li>We send transactional emails (password reset, weekly recap) through SendGrid.</li>
                        <li>You can delete your account and everything tied to it at any time — email <a className="underline text-[#2D5D4E]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-collect">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>What we collect</h2>
                    <p className="mb-4 text-[#515B57]">Only what the product needs to work for you.</p>
                    <ul className="space-y-3 text-[#1C2321]">
                        <li><span className="font-medium">Account:</span> email, name, hashed password (bcrypt), when you signed up.</li>
                        <li><span className="font-medium">Learning progress:</span> which lessons you've completed, XP, streak, placement level, game scores, grapheme misses (the phonics patterns you struggle with, so we can bring them back).</li>
                        <li><span className="font-medium">Preferences:</span> your chosen text-to-speech voice, whether autoplay is on, your practice-reminder time.</li>
                        <li><span className="font-medium">Read-aloud attempts:</span> we store the accuracy score and the passage you were reading, so you can see your trend over time. We do <span className="font-medium">not</span> store the raw audio.</li>
                        <li><span className="font-medium">Referral:</span> if a friend invited you, we remember their code so they can be credited.</li>
                        <li><span className="font-medium">Anonymous click events:</span> which buttons get tapped (e.g. "landing → register") so we can improve the product. No IP addresses, no fingerprinting.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-third-parties">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Who else touches your data</h2>
                    <p className="mb-4 text-[#515B57]">A small, deliberate list. Each one only sees the piece it needs.</p>
                    <ul className="space-y-3 text-[#1C2321]">
                        <li><span className="font-medium">OpenAI (TTS + Whisper):</span> when you tap a Listen button or record a read-aloud, we send that specific text or audio to OpenAI. Audio is transcribed and discarded on both ends.</li>
                        <li><span className="font-medium">Anthropic (Claude):</span> your messages to the AI tutor are sent to Anthropic to generate a reply. Nothing else about you is shared.</li>
                        <li><span className="font-medium">SendGrid:</span> transactional email — password resets and your weekly progress recap. Your email address only.</li>
                        <li><span className="font-medium">Stripe:</span> only used if you separately purchase one-on-one tutoring. The app itself is free.</li>
                        <li><span className="font-medium">MongoDB Atlas / hosting:</span> stores the account data described above, in a database only our servers can read.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-cookies">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Cookies and sessions</h2>
                    <p className="text-[#1C2321]">
                        We use exactly one cookie: <code className="px-1.5 py-0.5 rounded bg-[#F5F1E4] text-sm">rw_token</code>, which keeps you signed in. It's <span className="font-medium">httpOnly</span> and <span className="font-medium">Secure</span>, so no browser script (ours or anyone else's) can read it. No advertising cookies. No tracking pixels.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-children">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Children</h2>
                    <p className="text-[#1C2321]">
                        Lift Up Reading is designed for adult learners. We do not knowingly collect
                        information from anyone under 13. If you believe we have, email{" "}
                        <a className="underline text-[#2D5D4E]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        and we'll delete it.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-rights">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Your rights</h2>
                    <p className="mb-3 text-[#515B57]">
                        You can, at any time and for any reason:
                    </p>
                    <ul className="space-y-2 list-disc pl-5 text-[#1C2321]">
                        <li>See what we have on you.</li>
                        <li>Correct anything that's wrong.</li>
                        <li>Delete your account and everything tied to it.</li>
                        <li>Ask for a portable copy of your data (JSON export).</li>
                        <li>Turn off the weekly recap email or the daily practice reminder from your Profile page.</li>
                    </ul>
                    <p className="mt-4 text-[#1C2321]">
                        Email{" "}
                        <a className="underline text-[#2D5D4E]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                        and we'll act on it within 30 days.
                    </p>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-security">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>How we keep it safe</h2>
                    <ul className="space-y-2 list-disc pl-5 text-[#1C2321]">
                        <li>Passwords are hashed with bcrypt — we can never see the plaintext.</li>
                        <li>The whole site runs over HTTPS.</li>
                        <li>Your session is carried in an httpOnly cookie so it can't be stolen by a browser script.</li>
                        <li>Only our own servers can read the database. No public exports.</li>
                    </ul>
                </section>

                <section className="card-rw p-7 mb-6" data-testid="privacy-section-changes">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Changes to this policy</h2>
                    <p className="text-[#1C2321]">
                        If we ever change what we collect or who we share it with, we'll update the
                        "last updated" date at the top and email everyone with an account before the
                        change takes effect.
                    </p>
                </section>

                <section className="card-rw p-7" data-testid="privacy-section-contact">
                    <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: "Outfit" }}>Contact</h2>
                    <p className="text-[#1C2321]">
                        Questions, concerns, or a request about your data — email{" "}
                        <a
                            className="underline text-[#2D5D4E] font-medium"
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="privacy-contact-email"
                        >
                            {CONTACT_EMAIL}
                        </a>
                        . A real human reads it.
                    </p>
                </section>
            </div>
        </main>
    );
}
